import { Inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import * as moment from 'moment';

import { ToastController } from '@ionic/angular';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import write_blob from 'capacitor-blob-writer';
import { Zip } from '@ionic-native/zip/ngx';

const DOWNLOAD_DIRECTORY = 'Zilingo';
const ASSET_TYPES = ['png', 'pdf', 'xlsx', 'ASSET_TYPE_NOT_DEFINED'] as const;

type TDownLoadFileSystemTypes = typeof ASSET_TYPES[number];

@Injectable({
  providedIn: 'root',
})
export class DownloadFileSystemAppService {
  #isCapacitor = Capacitor.isNativePlatform();
  #zip;
  constructor(@Inject('Zip') zip: Zip, private fileOpener: FileOpener, private toastController: ToastController) {
    this.#zip = zip;
  }

  #checkAndCreateDir = (path: string, directory: Directory): Promise<boolean> => {
    return Filesystem.readdir({
      path: path,
      directory: directory,
    })
      .then(() => {
        return true;
      })
      .catch((error1) => {
        console.log('from checking directory', error1);
        return Filesystem.mkdir({
          path: path,
          directory: directory,
          recursive: true,
        })
          .then(() => {
            return true;
          })
          .catch((error) => {
            console.log('from creating directory', error);
            return false;
          });
      });
  };

  #checkAndRemoveDir = (path: string): Promise<boolean> => {
    return Filesystem.readdir({
      path: path,
      directory: Directory.External,
    })
      .then(() => {
        return Filesystem.rmdir({
          path: path,
          directory: Directory.External,
          recursive: true,
        })
          .then(() => true)
          .catch((error) => {
            console.log('from removing the directory', error);
            return false;
          });
      })
      .catch((error) => {
        console.log('from reading directory', error);
        return true;
      });
  };

  #presentToast = async (fileName: string, assetType: TDownLoadFileSystemTypes) => {
    const toast = await this.toastController.create({
      message: `${assetType.toUpperCase()} File ${fileName} Successfully Dowloaded`,
      duration: 10000,
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
            console.log('Cancel clicked');
          },
        },
      ],
      color: 'success',
    });
    toast.present();
  };

  #writeFile = (
    path: string,
    filename: string,
    content: string,
    assetType: TDownLoadFileSystemTypes,
  ): Promise<boolean> => {
    return Filesystem.writeFile({
      path: path + filename,
      data: content,
      directory: Directory.Documents,
      recursive: true,
    }).then(async (result) => {
      if (result) {
        this.#presentToast(filename, assetType);
        const file_uri = await Filesystem.getUri({
          directory: Directory.Documents,
          path: path + filename,
        });
        // NOTE: Map of all mimeType will help standardization in codebase
        switch (assetType) {
          case 'pdf':
            await this.fileOpener.open(file_uri.uri, 'application/pdf');
            break;
          case 'png':
            await this.fileOpener.open(file_uri.uri, 'image/png');
            break;
          case 'xlsx':
            await this.fileOpener.open(
              file_uri.uri,
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            );
            break;
          default:
            break;
        }

        return true;
      } else {
        return false;
      }
    });
  };

  downloadFilesInApp = (fileName: string, data: string, assetType: TDownLoadFileSystemTypes) => {
    if (this.#isCapacitor) {
      this.#checkAndCreateDir(DOWNLOAD_DIRECTORY, Directory.Documents).then((isFolderCreated) => {
        if (isFolderCreated) {
          this.#writeFile(
            DOWNLOAD_DIRECTORY,
            `${fileName}-${moment().format('--DD-MMMM-YYYY--h-mm-ss-A')}.${assetType}`,
            data,
            assetType,
          );
        } else {
          console.log('[ERROR] Problem making directory');
        }
      });
    } else {
      console.log('[ERROR] downloadFilesInApp is enabled only for Capacitor');
    }
  };

  #deleteFile = async (path: string) => {
    try {
      console.log('[path]', path);
      await Filesystem.deleteFile({
        path: path,
        directory: Directory.External,
      });
      console.log('[File deletion successful]', path);
      return true;
    } catch (error) {
      console.log('Error File Deletion', error);
      return false;
    }
  };

  writeSilhoutteAssetsInApp = async (data: Blob) => {
    if (this.#isCapacitor) {
      try {
        const silhouttesDirectoryPath = DOWNLOAD_DIRECTORY + '/Silhouttes';
        await this.#checkAndRemoveDir(silhouttesDirectoryPath);
        await this.#checkAndCreateDir(silhouttesDirectoryPath, Directory.External);
        const downloadedZipFilePath = await write_blob({
          path: silhouttesDirectoryPath + '/silhoutte.zip',
          directory: Directory.External,
          blob: data,
        });
        const pathToUnzipFile = await Filesystem.getUri({
          path: silhouttesDirectoryPath,
          directory: Directory.External,
        });
        console.log('[SOURCE ==> downloadedZipFilePath]', downloadedZipFilePath);
        console.log('[DESTINATION ==> pathToUnzipFile]', pathToUnzipFile.uri);
        const RES_CODE = await this.#zip.unzip(downloadedZipFilePath, pathToUnzipFile.uri);
        console.log('RES_CODE', RES_CODE);
        if (RES_CODE === 0) return await this.#deleteFile(silhouttesDirectoryPath + '/silhoutte.zip');
      } catch (error) {
        console.log('[Something went wrong]', error);
      }
    } else console.log('[ERROR] writeSilhoutteAssetsInApp is enabled only for Capacitor');
    return false;
  };

  getAssetType(format: string) {
    const hasFormat = ASSET_TYPES.find((v) => v === format);
    const assetType = hasFormat ? hasFormat : 'ASSET_TYPE_NOT_DEFINED';
    return assetType;
  }
}
